import User from 'iam/models/User'
import { Id } from 'types/definitions'
import { Model } from 'infra/model'
import { PrimeUser } from 'iam/UserStore'
import { DateZero, maybeParseDate } from 'utils/date-util'
import { TransactionType, parseTransactionType } from '../TransactionType'
import {
  TransactionSourceType,
  parseTransactionSourceType,
} from '../TransactionSourceType'
import {
  UserTransactionReason,
  parseUserTransactionReason,
} from '../UserTransactionReason'
import { ApprovedTransactionResource } from './dtos/drafting'
import { DraftPayout } from './DraftPayout'
import { PrimeWorkspace } from '../../iam/WorkspaceStore'
import Workspace from '../../iam/models/Workspace'
import { TransactionFlag, parseTransactionFlag } from '../TransactionFlag'

/**
 * A transaction that has been approved for payout.
 */
export class ApprovedTransaction extends Model<
  ApprovedTransactionResource,
  ApprovedTransaction
> {
  id = Id.none
  shortId = ''
  amount = 0
  createdAt = DateZero()
  transactedAt = DateZero()
  type = TransactionType.Payment
  recipient = User.none
  workspace = Workspace.none
  source = TransactionSourceType.Unknown

  reason: UserTransactionReason | null = UserTransactionReason.Unknown

  jobId: string | null = null

  flags: TransactionFlag[] = []

  approvedBy = User.none
  approvedAt = DateZero()

  /**
   * Constructor.
   */
  constructor(
    public readonly draft: DraftPayout,
    private primeUser: PrimeUser,
    private primeWorkspace: PrimeWorkspace,
  ) {
    super()
  }

  /**
   * @inheritDoc
   */
  parse(
    attrs: Partial<ApprovedTransactionResource>,
  ): Partial<ApprovedTransaction> {
    return {
      id: attrs.id,
      shortId: attrs.short_id,
      amount: attrs.amount,
      createdAt: maybeParseDate(attrs.date_created),
      transactedAt: maybeParseDate(attrs.date_transacted),
      approvedAt: maybeParseDate(attrs.date_approved),
      approvedBy: attrs.approved_by_user_id
        ? this.primeUser({ public_id: attrs.approved_by_user_id })
        : undefined,
      recipient: attrs.recipient_user_id
        ? this.primeUser({ public_id: attrs.recipient_user_id })
        : undefined,
      workspace: attrs.workspace_id
        ? this.primeWorkspace({ id: attrs.workspace_id })
        : undefined,
      type: attrs.type && parseTransactionType(attrs.type),
      source: attrs.source && parseTransactionSourceType(attrs.source),
      reason: attrs.reason && parseUserTransactionReason(attrs.reason),
      jobId: attrs.job_id,
      flags: attrs.flags?.map(parseTransactionFlag),
    }
  }
}
